import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

const SecurityTracking = () => {
  const token = localStorage.getItem('token');
  let user = null;
  try {
    const payload = token ? JSON.parse(atob(token.split('.')[1])) : null;
    user = payload ? { id: payload.id, role: payload.role } : null;
  } catch(e) {}

  const [tab, setTab] = useState('sessions');
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ success: '', limit: 50, range: 'today' });

  const getDateRange = (range) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    switch(range) {
      case 'today': return { start: today, end: today };
      case 'yesterday': {
        const y = new Date(now); y.setDate(y.getDate()-1);
        const yd = y.toISOString().split('T')[0];
        return { start: yd, end: yd };
      }
      case '7days': {
        const d = new Date(now); d.setDate(d.getDate()-7);
        return { start: d.toISOString().split('T')[0], end: today };
      }
      case '30days': {
        const d = new Date(now); d.setDate(d.getDate()-30);
        return { start: d.toISOString().split('T')[0], end: today };
      }
      case 'all': return { start: null, end: null };
      case 'custom': return { start: customStart || null, end: customEnd || null };
      default: return { start: today, end: today };
    }
  };
  const [forcingOut, setForcingOut] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const { start, end } = getDateRange(filter.range);
      const dateParams = start ? `&start=${start}&end=${end}` : '';
      const userParam = selectedUser ? `&user_id=${selectedUser.id}` : '';
      const [sumRes, sessRes, histRes] = await Promise.all([
        api.get('/security/summary'),
        api.get('/security/active-sessions'),
        api.get(`/security/login-history?limit=${filter.limit}${filter.success !== '' ? '&success='+filter.success : ''}${dateParams}${userParam}`)
      ]);
      setSummary(sumRes.data);
      setSessions(sessRes.data.sessions || []);
      setHistory(histRes.data.history || []);
      setHistoryStats(histRes.data.stats || null);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchAll();
    // Load users for search
    api.get('/users').then(r => {
      const users = r.data?.users || r.data?.data || r.data || [];
      if (Array.isArray(users)) setAllUsers(users);
    }).catch(() => {});
  }, []);
  useEffect(() => { if (!loading) fetchAll(); }, [filter]);

  const forceLogout = async (sessionId, userName) => {
    if (!window.confirm(`Force logout ${userName}? They will need to log in again.`)) return;
    setForcingOut(sessionId);
    try {
      await api.post(`/security/force-logout/${sessionId}`, {});
      fetchAll();
    } catch(e) { alert('Error forcing logout'); }
    setForcingOut(null);
  };

  const fmt = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const fmtLocation = (row) => {
    if (!row.city && !row.country) return '—';
    const parts = [row.city, row.region, row.country].filter(Boolean);
    return parts.join(', ');
  };

  const deviceIcon = (type) => type === 'mobile' ? '📱' : type === 'tablet' ? '📲' : '🖥️';
  const browserIcon = (b) => {
    if (!b) return '🌐';
    if (b === 'Chrome') return '🟡';
    if (b === 'Firefox') return '🦊';
    if (b === 'Safari') return '🧭';
    if (b === 'Edge') return '🔵';
    return '🌐';
  };

  const flagEmoji = (country) => {
    if (!country || country.length !== 2) return '🌍';
    return country.toUpperCase().split('').map(c =>
      String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
    ).join('');
  };

  if (loading) return (
    <Layout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:400, color:'#6B7280' }}>
        Loading security data...
      </div>
    </Layout>
  );

  return (
    <Layout>
    <div style={{ padding:'24px', maxWidth:1200, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:700, color:'#1F4027', margin:0 }}>🔐 Security Tracking</h1>
          <p style={{ color:'#6B7280', marginTop:4, fontSize:14 }}>Monitor user access, active sessions and login activity</p>
        </div>
        <button onClick={fetchAll}
          style={{ padding:'8px 16px', background:'#F3F4F6', border:'1px solid #E5E7EB',
            borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:600 }}>↻ Refresh</button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:16, marginBottom:24 }}>
          {[
            { label:'Active Sessions', value:summary.active_sessions, color:'#2D5C3F', bg:'#F0FDF4', icon:'🟢' },
            { label:'Logins (24h)', value:summary.logins_24h, color:'#1F4027', bg:'#F0FDF4', icon:'🔑' },
            { label:'Failed (24h)', value:summary.failed_24h, color:summary.failed_24h>5?'#DC2626':'#D97706', bg:summary.failed_24h>5?'#FEF2F2':'#FFFBEB', icon:'⚠️' },
            { label:'Failed (1h)', value:summary.failed_1h, color:summary.failed_1h>3?'#DC2626':'#6B7280', bg:summary.failed_1h>3?'#FEF2F2':'#F9FAFB', icon:'🚨' },
          ].map((c,i) => (
            <div key={i} style={{ background:c.bg, borderRadius:10, padding:'16px 20px', border:`1px solid ${c.color}22` }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{c.icon}</div>
              <div style={{ fontSize:28, fontWeight:700, color:c.color }}>{c.value}</div>
              <div style={{ fontSize:12, color:'#6B7280' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Failed Alert */}
      {summary?.recent_failed?.length > 0 && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'14px 18px', marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#DC2626', marginBottom:12 }}>
            🚨 Recent Failed Login Attempts
          </div>
          {summary.recent_failed.map((f,i) => (
            <div key={i} style={{ 
              padding:'10px 12px', borderRadius:8, marginBottom:6,
              background:'white', border:'1px solid #FEE2E2',
              display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, alignItems:'center' }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'#1A3A2A' }}>{f.email_attempted}</div>
                <div style={{ fontSize:11, color:'#DC2626', fontWeight:600 }}>
                  ✗ {f.failure_reason?.replace(/_/g,' ') || 'failed'}
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, color:'#374151' }}>
                  {deviceIcon(f.device_type)} {browserIcon(f.browser)} {f.browser || '—'} · {f.os || '—'}
                </div>
                <div style={{ fontSize:11, color:'#6B7280', fontFamily:'monospace' }}>{f.ip_address}</div>
              </div>
              <div>
                {f.city && f.country !== 'LO' ? (
                  <div>
                    <div style={{ fontSize:12, color:'#374151' }}>{flagEmoji(f.country)} {f.city}{f.country ? `, ${f.country}` : ''}</div>
                    {f.isp && <div style={{ fontSize:11, color:'#9CA3AF' }}>{f.isp}</div>}
                  </div>
                ) : <span style={{ fontSize:11, color:'#9CA3AF' }}>Local/Private</span>}
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:11, color:'#9CA3AF' }}>{fmt(f.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid #E5E7EB' }}>
        {[
          { id:'sessions', label:`🟢 Active Sessions (${sessions.length})` },
          { id:'history', label:'📋 Login History' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'10px 20px', border:'none', background:'none', cursor:'pointer',
              fontSize:14, fontWeight:600,
              color:tab===t.id?'#1F4027':'#6B7280',
              borderBottom:tab===t.id?'2px solid #1F4027':'2px solid transparent',
              marginBottom:-2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ACTIVE SESSIONS */}
      {tab === 'sessions' && (
        <div>
          {sessions.length === 0 ? (
            <div style={{ textAlign:'center', padding:60, color:'#9CA3AF', background:'white', borderRadius:10, border:'1px solid #E5E7EB' }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🔒</div>
              <div>No active sessions found</div>
              <div style={{ fontSize:12, marginTop:4 }}>Sessions appear here after users log in</div>
            </div>
          ) : (
            <div style={{ background:'white', borderRadius:10, border:'1px solid #E5E7EB', overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                <thead>
                  <tr style={{ background:'#1F4027' }}>
                    {['User','Role','Device & Browser','OS','Location / ISP','IP Address','Session Start','Last Active','Action'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'white', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s,i) => (
                    <tr key={s.id} style={{ background:i%2===0?'white':'#F9FAFB', borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#1A3A2A' }}>{s.user_name}</div>
                        <div style={{ fontSize:11, color:'#6B7280' }}>{s.email}</div>
                      </td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10, background:'#E8F5E9', color:'#2D5C3F' }}>{s.role}</span>
                      </td>
                      <td style={{ padding:'10px 14px', fontSize:13 }}>
                        {deviceIcon(s.device_type)} {browserIcon(s.browser)} {s.browser || '—'}
                      </td>
                      <td style={{ padding:'10px 14px', fontSize:12, color:'#6B7280' }}>{s.os || '—'}</td>
                      <td style={{ padding:'10px 14px', fontSize:12 }}>
                        {s.country && s.country !== 'LO' ? (
                          <div>
                            <div>{flagEmoji(s.country)} {s.city || '—'}{s.region ? `, ${s.region}` : ''}, {s.country}</div>
                            {s.isp && <div style={{fontSize:10,color:'#9CA3AF'}}>{s.isp}</div>}
                          </div>
                        ) : <span style={{color:'#9CA3AF'}}>Local/Private</span>}
                      </td>
                      <td style={{ padding:'10px 14px', fontSize:12, color:'#6B7280', fontFamily:'monospace' }}>{s.ip_address}</td>
                      <td style={{ padding:'10px 14px', fontSize:11, color:'#6B7280', whiteSpace:'nowrap' }}>{fmt(s.created_at)}</td>
                      <td style={{ padding:'10px 14px', fontSize:11, color:'#2D5C3F', whiteSpace:'nowrap' }}>{fmt(s.last_active)}</td>
                      <td style={{ padding:'10px 14px' }}>
                        {s.user_id !== user?.id ? (
                          <button onClick={() => forceLogout(s.id, s.user_name)} disabled={forcingOut===s.id}
                            style={{ padding:'5px 12px', background:forcingOut===s.id?'#9CA3AF':'#DC2626',
                              color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {forcingOut===s.id?'...':'Force Out'}
                          </button>
                        ) : (
                          <span style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic' }}>You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LOGIN HISTORY */}
      {tab === 'history' && (
        <div>
          {historyStats && (
            <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
              {[
                { label:'Total', value:historyStats.total },
                { label:'Successful', value:historyStats.successful, color:'#16A34A' },
                { label:'Failed', value:historyStats.failed, color:parseInt(historyStats.failed)>10?'#DC2626':'#D97706' },
                { label:'Unique Users', value:historyStats.unique_users },
              ].map((s,i) => (
                <div key={i} style={{ background:'white', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 16px', minWidth:110 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:s.color||'#1F4027' }}>{s.value}</div>
                  <div style={{ fontSize:11, color:'#6B7280' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* User Search */}
          <div style={{ position:'relative', marginBottom:14 }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ position:'relative', flex:1, maxWidth:300 }}>
                <input
                  value={userSearch}
                  onChange={e => {
                    const val = e.target.value;
                    setUserSearch(val);
                    if (val.length >= 2) {
                      const matches = allUsers.filter(u =>
                        `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(val.toLowerCase())
                      ).slice(0, 6);
                      setUserSuggestions(matches);
                    } else {
                      setUserSuggestions([]);
                    }
                    if (!val) { setSelectedUser(null); }
                  }}
                  placeholder="Search user (type 2+ chars)..."
                  style={{ width:'100%', padding:'8px 12px', borderRadius:8,
                    border: selectedUser ? '2px solid #2D5C3F' : '1px solid #D1D5DB',
                    fontSize:13, boxSizing:'border-box' }}
                />
                {userSuggestions.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100,
                    background:'white', border:'1px solid #E5E7EB', borderRadius:8,
                    boxShadow:'0 4px 12px rgba(0,0,0,0.1)', overflow:'hidden' }}>
                    {userSuggestions.map(u => (
                      <div key={u.id}
                        onClick={() => {
                          setSelectedUser(u);
                          setUserSearch(`${u.first_name} ${u.last_name}`);
                          setUserSuggestions([]);
                        }}
                        style={{ padding:'8px 12px', cursor:'pointer', fontSize:13,
                          borderBottom:'1px solid #F3F4F6',
                          display:'flex', justifyContent:'space-between', alignItems:'center' }}
                        onMouseEnter={e => e.currentTarget.style.background='#F0FDF4'}
                        onMouseLeave={e => e.currentTarget.style.background='white'}>
                        <div>
                          <span style={{ fontWeight:600 }}>{u.first_name} {u.last_name}</span>
                          <span style={{ color:'#9CA3AF', marginLeft:8, fontSize:11 }}>{u.email}</span>
                        </div>
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:8,
                          background:'#E8F5E9', color:'#2D5C3F', fontWeight:600 }}>{u.role}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedUser && (
                <button onClick={() => { setSelectedUser(null); setUserSearch(''); }}
                  style={{ padding:'7px 12px', background:'#FEF2F2', color:'#DC2626',
                    border:'1px solid #FECACA', borderRadius:8, fontSize:12,
                    fontWeight:600, cursor:'pointer' }}>
                  ✕ Clear
                </button>
              )}
            </div>
          </div>

          {/* Quick Range Tabs */}
          <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
            {[
              { id:'today', label:'Today' },
              { id:'yesterday', label:'Yesterday' },
              { id:'7days', label:'Last 7 Days' },
              { id:'30days', label:'Last 30 Days' },
              { id:'all', label:'All Time' },
              { id:'custom', label:'📅 Custom' },
            ].map(r => (
              <button key={r.id} onClick={() => setFilter(f=>({...f, range:r.id}))}
                style={{ padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer',
                  fontSize:13, fontWeight:600,
                  background: filter.range === r.id ? '#1F4027' : '#F3F4F6',
                  color: filter.range === r.id ? 'white' : '#374151' }}>
                {r.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range */}
          {filter.range === 'custom' && (
            <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <label style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>From:</label>
                <input type="date" value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  style={{ padding:'7px 10px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <label style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>To:</label>
                <input type="date" value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  style={{ padding:'7px 10px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }} />
              </div>
              <button onClick={fetchAll}
                style={{ padding:'7px 14px', background:'#1F4027', color:'white',
                  border:'none', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                Apply
              </button>
            </div>
          )}

          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
            <select value={filter.success} onChange={e => setFilter(f=>({...f,success:e.target.value}))}
              style={{ padding:'7px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }}>
              <option value="">All attempts</option>
              <option value="true">Successful only</option>
              <option value="false">Failed only</option>
            </select>
            <select value={filter.limit} onChange={e => setFilter(f=>({...f,limit:e.target.value}))}
              style={{ padding:'7px 12px', borderRadius:6, border:'1px solid #D1D5DB', fontSize:13 }}>
              <option value={25}>Last 25</option>
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
            </select>
          </div>

          {history.length === 0 ? (
            <div style={{ textAlign:'center', padding:60, color:'#9CA3AF', background:'white', borderRadius:10, border:'1px solid #E5E7EB' }}>No login history yet</div>
          ) : (
            <div style={{ background:'white', borderRadius:10, border:'1px solid #E5E7EB', overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                <thead>
                  <tr style={{ background:'#1F4027' }}>
                    {['Date & Time','User','Result','Reason','Device','Browser','OS','Location','IP'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:12, fontWeight:600, color:'white', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((h,i) => (
                    <tr key={h.id} style={{ background:i%2===0?'white':'#F9FAFB', borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'9px 14px', fontSize:11, color:'#6B7280', whiteSpace:'nowrap' }}>{fmt(h.created_at)}</td>
                      <td style={{ padding:'9px 14px' }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#1A3A2A' }}>{h.user_name||'—'}</div>
                        <div style={{ fontSize:11, color:'#6B7280' }}>{h.email_attempted}</div>
                      </td>
                      <td style={{ padding:'9px 14px' }}>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10,
                          background:h.success?'#DCFCE7':'#FEE2E2', color:h.success?'#16A34A':'#DC2626' }}>
                          {h.success?'✓ Success':'✗ Failed'}
                        </span>
                      </td>
                      <td style={{ padding:'9px 14px', fontSize:12, color:'#6B7280' }}>{h.failure_reason?h.failure_reason.replace(/_/g,' '):'—'}</td>
                      <td style={{ padding:'9px 14px', fontSize:13 }}>{deviceIcon(h.device_type)} {h.device_type||'—'}</td>
                      <td style={{ padding:'9px 14px', fontSize:12 }}>{browserIcon(h.browser)} {h.browser||'—'}</td>
                      <td style={{ padding:'9px 14px', fontSize:12, color:'#6B7280' }}>{h.os||'—'}</td>
                      <td style={{ padding:'9px 14px', fontSize:12 }}>
                        {h.country && h.country !== 'LO' ? (
                          <div>
                            <div>{flagEmoji(h.country)} {h.city||'—'}, {h.country}</div>
                            {h.isp && <div style={{fontSize:10,color:'#9CA3AF'}}>{h.isp}</div>}
                          </div>
                        ) : <span style={{color:'#9CA3AF'}}>Local</span>}
                      </td>
                      <td style={{ padding:'9px 14px', fontSize:11, color:'#6B7280', fontFamily:'monospace' }}>{h.ip_address||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
    </Layout>
  );
};

export default SecurityTracking;
